import Modal from "../../common/modal";
import InputText from "../../common/inputText";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import InputTextArea from "../../common/inputTextArea";
import { useParams } from "react-router-dom";
import { getToken } from "../../../context/auth";
import Loading from "../../common/loading";
import ButtonText from "../../common/buttonText";
import debounce from "lodash/debounce";
import { searchUserByPhoneNumber } from "../../../api/users";
import { useMessages } from "context/messages";
import { addUserToPermitQueue, editPermitQueueItem } from "../../../api/permitQueue";
import { useParkingArea } from "../../../context/parkingArea";
import { PermitQueueType } from "../../../helpers/consts";
import { useTranslation } from "react-i18next";
import InputPhoneNumber from "../../common/inputPhoneNumber";
import countries from "../../../helpers/countries";

function PermitQueueItemModal({ display, setDisplay, queueItems, queueItem, onAddQueueItem, onEditQueueItem, queueType }) {
    const { showToast } = useMessages();
    const [ itemDto, setItemDto ] = useState({
        userExtId: null,
        description: "",
        permitQueueType: queueType ?? PermitQueueType.AreaParking
    });
    const [ errors, setErrors ] = useState({});
    const [ submitAsync, setSubmitAsync ] = useState(false);
    
    const [ initialPhoneNumber, setInitialPhoneNumber ] = useState(queueItem?.phone ?? '');
    const initialCountry = useRef(countries.find((c) => queueItem?.phone?.startsWith(c.code)));
    const [ phoneNumber, setPhoneNumber ] = useState('');
    const [ userSearchAsync, setUserSearchAsync ] = useState(queueItem?.phone?.length > 4);
    const [ dbUser, setDbUser ] = useState(undefined);
    const [userAlreadyInQueue, setuserAlreadyInQueue] = useState(false)
    
    const [ userDto, setUserDto ] = useState({
        email: '',
        firstName: '',
        lastName: '',
    });

    const { organizationExtId, parkingAreaExtId } = useParams();

    const { parkingArea } = useParkingArea();

    const { t } = useTranslation();

    const userExistsInAirPark = Boolean(dbUser);

    useEffect(() => {
      const debounceTimer = setTimeout(() => {
        const exists = queueItems.some(
          (item) =>
            (item.email === userDto.email || item.phone === phoneNumber) &&
            phoneNumber &&
            userDto?.email
        );
        setuserAlreadyInQueue(exists);
      }, 800);

      return () => clearTimeout(debounceTimer);
    }, [userDto?.email, phoneNumber]); 

    const permitTypeOptions = useMemo(() => {
        return [
            {
                title: t('createPermitPage.allParkingSpots'),
                value: PermitQueueType.AreaParking
            }, {
                title: t('createPermitPage.specificParkingSpots'),
                value: PermitQueueType.AnyNumberedSpot
            }
        ];
    }, []);

    const onSubmit = () => {
        setErrors({});
        setSubmitAsync(true);
        if (isEdit) {
            const editPermitQueueItemDto = {
                description: itemDto.description,
                phone: phoneNumber,
                ...userDto,
            }
            getToken().then((token) => {
                editPermitQueueItem(token, editPermitQueueItemDto, queueItem.permitQueueExtId)
                    .then((response) => {
                        onEditQueueItem(response);
                        setDisplay(false);
                    })
                    .finally(() => setSubmitAsync(false))
            })
        } else {
            if (!phoneNumber.length) {
                setErrors({ ...errors, user: true })
                setSubmitAsync(false);
                return;
            }
            const addToPermitQueueDto = {
                ...itemDto,
                parkingAreaExtId,
            };

            if (dbUser) {
                addToPermitQueueDto.userExtId = dbUser.userExtId;
                addToPermitQueueDto.email = dbUser.email;
                addToPermitQueueDto.phone = dbUser.phone;
                addToPermitQueueDto.firstName = dbUser.firstName;
                addToPermitQueueDto.lastName = dbUser.lastName;
            } else {
                Object.entries(userDto).forEach(([ key, value ]) => {
                    addToPermitQueueDto[key] = value;
                })
                addToPermitQueueDto.phone = phoneNumber;
            }

            getToken().then((token) => {
                addUserToPermitQueue(token, addToPermitQueueDto)
                    .then((response) => {
                        if (response && response.error) {
                            showToast(
                                t('generic.errorTitle'),
                                response.errorMessage,
                                'error'
                            );
                        } else {
                            onAddQueueItem(response);
                            setDisplay(false); 
                        }
                    })
                    .finally(() => setSubmitAsync(false))
            })
        }
    }

    const isEdit = queueItem !== null;

    const searchUser = useCallback((phoneNumber) => {
        return getToken().then((token) => {
            setUserSearchAsync(true);
            return searchUserByPhoneNumber(token, phoneNumber)
                .then((res) => {
                    if (res?.error) {
                        setDbUser(null);
                        setUserDto((prevDto) => ({
                            email:  prevDto?.email || queueItem?.email || '',
                            firstName: prevDto?.firstName || queueItem?.firstName || '',
                            lastName: prevDto?.lastName || queueItem?.lastName || '',
                        }));
                        setErrors({ ...errors, phone: true });
                        return null;
                    } else {
                        setDbUser(res);
                        setUserDto({
                            email: res?.email ?? '',
                            firstName: res?.firstName ?? '',
                            lastName: res?.lastName ?? '',
                        });
                        setErrors({ ...errors, phone: false });
                        return res;
                    }
                })
                .finally(() => {
                    setUserSearchAsync(false);
                })
        });
    }, [queueItem])

    const debouncedUserSearch = React.useRef(
        debounce((phoneNumber) => searchUser(phoneNumber), 1500)
    ).current;

    const userSection = () => {
        const searchedForUser = phoneNumber.length > 4 && !userSearchAsync && dbUser !== undefined;

        const inputDisabled = userExistsInAirPark || userSearchAsync;

        return (
            <div>
                {userSearchAsync ? (
                    <div className='flex justify-between my-1'>
                        <Loading size={5} />
                    </div>
                ) : searchedForUser && (
                    dbUser ? (
                        <div className='font-medium text-airpark-green my-2'>{t('permitQueueItemModal.userInSystem')}</div>
                    ) : (
                        <div className='font-medium text-airpark-red my-2'>{t('permitQueueItemModal.userNotInSystem')}</div>
                    )
                )}
                {searchedForUser && userAlreadyInQueue && 
                    <div className='font-medium text-airpark-red my-2'>{t('permitQueueItemModal.userAlreadyInQueue')}</div>
                }
                
                <div>
                    <InputText title={t('permitQueueItemModal.email')}
                               className='mb-3'
                               disabled={inputDisabled || isEdit}
                               value={userDto.email}
                               onChange={(email) => setUserDto({ ...userDto, email })}
                    />
                    <InputText title={t('permitQueueItemModal.firstName')}
                               className='mb-3'
                               disabled={inputDisabled}
                               value={userDto.firstName}
                               onChange={(firstName) => setUserDto({ ...userDto, firstName })}
                    />
                    <InputText title={t('permitQueueItemModal.lastName')}
                               className='mb-3'
                               disabled={inputDisabled}
                               value={userDto.lastName}
                               onChange={(lastName) => setUserDto({ ...userDto, lastName })}
                    />
                </div>
            </div>
        )
    }

    useEffect(() => {
        if (phoneNumber.length > 4) {
            debouncedUserSearch(phoneNumber);
        }
    }, [ phoneNumber ]);

    useEffect(() => {
        if (queueItem) {
            setItemDto({ ...queueItem });
            if (queueItem.phone) {
                setPhoneNumber(queueItem.phone);
            }
        }
    }, []);

    return (
        <Modal title={isEdit ? t('permitQueueItemModal.titleEdit') : t('permitQueueItemModal.titleAdd')}
               overlayClassName={'items-center justify-center'}
               modalClassName={'max-h-[96vh] overflow-y-auto flex-col z-50'}
        innerClassName={"w-[95vw] md:w-[70vw] overflow-y-auto"}
        display={display}
               setDisplay={setDisplay}>
            <div className="mb-4">
            {parkingArea && <InputText disabled={true} value={parkingArea.name} title={t('permitQueueItemModal.parkingArea')} />}
            </div>
          <InputPhoneNumber
            title={
                    <div className='flex justify-between'>
                        <span className='mr-2'>{t('permitQueueItemModal.inputPhone')}</span>
              </div>
            }
                disabled={isEdit}
            setPhoneNumber={(phoneNumber, code) => {
                    setPhoneNumber(code + phoneNumber.replace(/^0+/, ''))
            }}
                defaultCountryCode={initialCountry.current?.country ?? 'SE'}
                defaultNumber={initialPhoneNumber.replace(initialCountry.current?.code, '')}
                resetTrigger={false}/>
        <div className={"mb-6 mt-1"}>{userSection()}</div>
        <InputTextArea
                title={t('permitQueueItemModal.inputSpecialRequests')}
                placeHolder={''}
          value={itemDto.description}
          onChange={(value) => {
                    setItemDto({ ...itemDto, description: value })
          }}
        />
        <ButtonText onClick={onSubmit}
            disabled={userAlreadyInQueue && !isEdit}
            isLoading={submitAsync}
            className="flex mt-6 justify-center ml-auto">
            {t('permitQueueItemModal.buttonSave')}
        </ButtonText>
      </Modal>
    )
}

export default PermitQueueItemModal;